import {Manifest, ManifestVersionChecker, ScoNavItem} from "./manifest";
import * as Message from "../message";
import * as Path from "../path";
import * as Sco from "./sco";
import * as Storage from "./storage";
import {TableOfContents} from "./toc";
import {SSLAApiEvents} from "./events";
import {ScormLog} from "./scormlog";
import {SSLAConfiguration} from "../config";
import {AiccScript} from "./aiccscript_parser";
import * as openLib from "../open";
import {DomRender} from "../render";
import {SSLAPlugin} from "../plugin";
import {rollupScores} from "./score_rollup";
import {NavActions} from "./sn";
import {rollupAllScoStatuses} from "./status_rollup";

export {UrlVarService} from "../url";

declare var reqwest: any;


export interface ScormPlugin {
    createManifestClass(): Manifest;

    endPreviousSco(): void;

    initializeApi(scoNav: ScoNavItem): void;

    initializeScosData(): void;

    initializeSequencing(): void;

    navigate(type: string): void;

    navigateByIdentifier(id: string): void;

    loadSco(scoNav: ScoNavItem, scoIndex: number): void;

    loadScoByIdentifier(scoId: string): void;

    performLayoutUpdate(): void;

    setupNavigationType(): void;

    standardLayoutUpdater(): void;

    updateManifestSelectability(): void;

    updateRollupScore(): void;

    onceLoaded(): void;

    start(): void;
}


export abstract class ScormPluginBase extends SSLAPlugin implements ScormPlugin {
    public assetCached = false;
    public events: SSLAApiEvents = null;
    public config: SSLAConfiguration = null;
    public manifest: Manifest;
    public mvc: ManifestVersionChecker;
    public currentScoIndex = -1;
    public currentSco: ScoNavItem = null;
    public nextScoIndex = -1;
    public nextScoAction: NavActions = null;
    public nextScoTarget = "";
    public storage: Storage.StorageInterface;
    public scormLog: ScormLog;
    public scosData: Sco.AllScosData = null;
    public toc: TableOfContents = null;

    abstract attemptSequencing(): string;

    abstract createManifestClass(): Manifest;

    abstract endPreviousSco(): void;

    abstract initializeApi(scoNav: ScoNavItem): void;

    abstract initializeScosData(): void;

    abstract initializeSequencing(): void;

    abstract navigate(type: string): void;

    abstract navigateByIdentifier(id: string): void;

    constructor(events: SSLAApiEvents = null) {
        super();

        if (events) {
            this.events = events;
        } else {
            this.events = new SSLAApiEvents();
        }
        // TODO This is a hack until we can move adl.nav.request resolution via maybeNavigate* to after int.htm is loaded.
        this.events.postCommit.add(this.updateManifestSelectability, this);
        this.events.postFinish.add(this.updateManifestSelectability, this);
        this.events.statusChange.add(this.updateManifestSelectability, this);
        this.events.statusChange.add(this.updateRollupStatus, this);
        this.events.scoreChange.add(this.updateRollupScore, this);
        this.scormLog = new ScormLog(this.events);
    }

    attemptNavigate() {
        // Attempting to navigate just involves putting ourselves in a position where we have fully flushed
        // the current SCO and can decide if we should be loading something next.
        openLib.openInterstitial(this.config);
    }

    checkManifestVersion(): boolean {
        this.mvc = new ManifestVersionChecker();
        let res: any;
        if (this.config.scormManifestUseRawXml()) {
            res = this.mvc.loadScormManifestFromString(this.config.scormManifestRawXml());
        } else {
            res = this.mvc.loadScormManifestFromDom(this.contents);
        }

        if (res === false) {
            return null;
        }

        if (this.mvc.schemaVersionMain == "1.2") {
        } else if (this.mvc.schemaVersionMain == "2004") {
        } else {
            return null;
        }
    }

    closeActiveContent() {
        this.nextScoAction = NavActions.Exit;
        this.nextScoTarget = "";
        openLib.closeCurrentActivity(this.config);
    }

    finish() {
        this.storage.notifyPlayerExit();
        this.closeActiveContent();
        // TODO We want to make sure everything gets suspended.  But, if we do it here, and a course only sends
        //   all its data after it unloads, will it happen too soon?
        //   What if on finish, we could tell it was due to the main window closing, or a more reasonable user event?
        //   Could we choose to wait on the sequencing?
        this.nextScoAction = NavActions.SuspendAll;
        this.nextScoTarget = "";
        this.attemptSequencing();
        // Since finish right now only occurs on unload-related activities, force
        // all the saving behavior we can possibly force.
        this.endPreviousSco();
    }

    interstitialReady2() {
        // Is there a previous SCO to make sure is fully closed?
        if (this.currentScoIndex >= 0) {
            // Has the previous SCO terminated?
            this.nextScoIndex = -1;
            this.endPreviousSco();
            this.currentScoIndex = null;
            this.currentSco = null;
            this.toc.currentScoId = "";
            this.scosData.currentScoId = "";
            this.updateManifestSelectability();
            this.toc.updateCurrentActivity("");
        }
        this.loadWhat();
    }

    interstitialReady() {
        // If we're in popup mode, make sure the popup is closed before continuing.
        if (this.config.openContentIn() == "popup") {
            let handle = setInterval(function () {
                if (!openLib.getPopupHandle()) {
                    clearInterval(handle);
                    this.interstitialReady2();
                }
            }.bind(this), 100);
        } else {
            this.interstitialReady2();
        }
    }

    loadWhat() {
        let nextStep = this.attemptSequencing();
        if (nextStep) {
            // Find the SCO and load it.
            for (let i = 0; i < this.manifest.navigableScoPreOrderTraversal.length; i++) {
                if (this.manifest.navigableScoPreOrderTraversal[i].identifier == nextStep) {
                    this.loadScoOnceReady(i);
                    break;
                }
            }
        }
        // Reset previously loaded state.
        this.nextScoAction = null;
        this.nextScoTarget = "";
    }

    loadScoOnceReady(scoIndex: number) {
        // Don't load any SCO out of range.
        if (scoIndex < 0) {
            return;
        }
        let scoNav = this.manifest.navigableScoPreOrderTraversal[scoIndex];
        this.updateManifestSelectability();
        this.events.preApiCreation.dispatch();
        this.initializeApi(scoNav);
        this.events.postApiCreation.dispatch();
        this.currentScoIndex = scoIndex;
        this.currentSco = scoNav;
        this.toc.currentScoId = this.currentSco.identifier;
        this.scosData.currentScoId = this.currentSco.identifier;
        this.scosData.scos[this.currentSco.identifier].startViewing();
        let launchUrl = this.manifest.scoLaunchUrl(scoNav);
        openLib.openSco(Path.joinQuerySafe([this.config.courseDirectory()], launchUrl), this.config, this.manifest.navigableScoPreOrderTraversal.length <= 1);
        (<HTMLButtonElement>document.getElementById("prev")).disabled = (this.currentScoIndex === 0) ? true : false;
        (<HTMLButtonElement>document.getElementById("next")).disabled = (this.manifest.navigableScoPreOrderTraversal.length <= this.currentScoIndex + 1) ? true : false;
        // Set empty status array for updating the navigation
        let statusArray: string[] = [];
        this.events.statusChange.dispatch(statusArray);
    }

    loadSco(scoNav: ScoNavItem, scoIndex: number) {
        alert("loadSco DEPRECATED, PLEASE REMOVE");
        console.log("loadSco DEPRECATED, PLEASE REMOVE");
        // console.log("loadSco?");
        // if (!this.config.reenterActivity() && scoIndex == this.currentScoIndex) {
        //     return;
        // }
        // this.nextScoIndex = scoIndex;
        // if (this.currentSco) {
        //     openLib.closeCurrentActivity(this.config);
        // }
        // openLib.openInterstitial(this.config);
        // //this.endPreviousSco(this.loadScoOnceReady.bind(this, scoNav, scoIndex));
    }

    loadScoByIdentifier(scoId: string) {
        alert("loadScoByIdentifier DEPRECATED, PLEASE REMOVE");
        console.log("loadScoByIdentifier DEPRECATED, PLEASE REMOVE");
    }

    protected clearManifestAiccScriptParsers() {
        let scos: ScoNavItem[] = this.manifest.navigableScoPreOrderTraversal;
        for (let i: number = 0; i < scos.length; i++) {
            let scoNav: ScoNavItem = scos[i];
            scoNav.aiccScriptParser = null;
        }
    }

    performLayoutUpdate() {
        if (this.config.layoutUpdater() != null) {
            this.config.layoutUpdater()(this);
        } else {
            this.standardLayoutUpdater();
        }
    }

    setupNavigationType() {
        let navigationType: string = this.config.navigationType();
        let scos: ScoNavItem[] = this.manifest.navigableScoPreOrderTraversal;
        if (navigationType == "") {
            // Do nothing, it's valid and doesn't override the manifest's behavior.
        } else {
            // TODO: This should probably be a service/factory so it's less intrusive to add.  Or part of the sequencing.
            this.clearManifestAiccScriptParsers();
            if (navigationType == "lockstep") {
                for (let i: number = 1; i < scos.length; i++) {
                    let scoNav: ScoNavItem = scos[i];
                    let asp: AiccScript = new AiccScript();
                    asp.parse(scos[i - 1].identifier);
                    scoNav.aiccScriptParser = asp;
                }
            } else if (navigationType == "examaftercontent") {
                if (scos.length > 1) {
                    let scoNav: ScoNavItem = scos[scos.length - 1];
                    let idents: string[] = [];
                    for (let i: number = 0; i < scos.length - 1; i++) {
                        idents.push(scos[i].identifier);
                    }
                    let asp: AiccScript = new AiccScript();
                    asp.parse(idents.join("&"));
                    scoNav.aiccScriptParser = asp;
                }
            } else if (navigationType == "onlyonce") {
                for (let i: number = 0; i < scos.length; i++) {
                    let scoNav: ScoNavItem = scos[i];
                    let asp: AiccScript = new AiccScript();
                    asp.parse(scoNav.identifier + "=n");
                    scoNav.aiccScriptParser = asp;
                }
            } else {
                Message.show(this.config, Message.Error, "Unrecognized nagivationType: " + navigationType);
            }
        }
    }

    // TODO This probably belongs in an external class.
    standardLayoutUpdater() {
        let body: HTMLElement = document.body;
        DomRender.removeClass(body, "hide-menu");
        DomRender.removeClass(body, "hide-toc");
        if (this.manifest.navigableScoPreOrderTraversal.length == 1) {
            if (this.config.singleScoView() == "HIDE_ALL" || this.config.singleScoView() == "HIDE_NAV_BUTTONS") {
                DomRender.addClass(body, "hide-menu");
            }
            if (this.config.singleScoView() == "HIDE_ALL" || this.config.singleScoView() == "HIDE_TREE") {
                DomRender.addClass(body, "hide-toc");
            }
        } else {
            if (this.config.multiScoView() == "HIDE_ALL" || this.config.multiScoView() == "HIDE_NAV_BUTTONS") {
                DomRender.addClass(body, "hide-menu");
            }
            if (this.config.multiScoView() == "HIDE_ALL" || this.config.multiScoView() == "HIDE_TREE") {
                DomRender.addClass(body, "hide-toc");
            }
        }
    }

    updateManifestSelectability() {
        for (let i: number = 0; i < this.manifest.navigableScoPreOrderTraversal.length; i++) {
            let scoNav: ScoNavItem = this.manifest.navigableScoPreOrderTraversal[i];
            // TODO Move this to a sequencing class.
            if (scoNav.aiccScriptParser != null) {
                scoNav.isSelectable = scoNav.aiccScriptParser.evaluate(null, this.scosData);
            }
            // TODO Move this to a sequencing class.
            else if (this.manifest.resourcesByIdentifierRef[scoNav.identifierRef]) {
                scoNav.isSelectable = true;
            }
            this.toc.updateSelectability(scoNav);
        }
    }

    updateRollupScore() {
        const rollupScore: string = rollupScores(this.scosData, this.config, this.manifest, "raw");
        const rollupNormalizedScore: string = rollupScores(this.scosData, this.config, this.manifest, "normalized");
        this.scosData.setRollupScore(rollupScore);
        this.scosData.setRollupNormalizedScore(rollupNormalizedScore);
        this.events.topLevelScoreChange.dispatch({
            raw: rollupScore,
            scaled: rollupNormalizedScore,
        });
    }

    updateRollupStatus() {
        const rollupStatus: string = rollupAllScoStatuses(this.scosData, this.config, this.manifest);
        this.scosData.setRollupStatus(rollupStatus);
        this.events.topLevelStatusChange.dispatch(rollupStatus);
    }

    onceLoaded() {
        let i: string;
        this.scosData.startViewing();
        for (i in this.scosData.scos) {
            var statusArray: string[] = [this.scosData.scos[i].getStatus()];
            this.toc.updateActivityStatus(i, statusArray);
        }
        this.updateManifestSelectability();
        this.initializeSequencing();
        (<any>window).DATA = this.scosData;
    }

    getMainAsset(callbackOnDecision: Function) {
        let me = this;
        reqwest({
            url: Path.join([this.config.courseDirectory(), this.config.scormManifestFilename()])
            , type: 'xml'
        })
            .then(function (resp: any) {
                me.assetCached = true;
                me.contents = resp;
            })
            .always(function (resp: any) {
                me.checked = true;
                callbackOnDecision();
            });
    }

    start() {
        if (!this.assetCached) {
            this.getMainAsset(this.loadAfterMainAsset.bind(this));
        } else {
            this.loadAfterMainAsset();
        }
    }

    loadAfterMainAsset() {
        this.postRender();
    }

    postRender() {
        let manifestParsed: boolean;
        let tocHtml: string;
        this.checkManifestVersion();
        if (!this.mvc) {
            Message.show(this.config, Message.Error, "The manifest (imsmanifest.xml) was not found or is unavailable.  Please check your settings and try again.");
            return;
        }

        try {
            this.manifest = this.createManifestClass();
            // TODO Consolidate this into createManifestClass.
            if (!this.manifest) {
                Message.show(this.config, Message.Error, "The manifest (imsmanifest.xml) was not found or is unavailable.  Please check your settings and try again.");
                return;
            }
        } catch (e) {
            Message.show(this.config, Message.Error, e.toString());
            return;
        }
        manifestParsed = this.manifest.processManifest();
        if (!manifestParsed) {
            Message.show(this.config, Message.Error, "The manifest did not parse correctly.  Reason: " + this.manifest.lastError);
            return;
        }
        this.setupNavigationType();
        this.initializeScosData();

        this.toc = new TableOfContents(this.config, this.events);
        tocHtml = this.toc.render(this.manifest.resourcesByIdentifierRef, this.manifest.itemTree);
        document.getElementById("toc").innerHTML = tocHtml;

        // We have all the pieces we need to control the layout of the screen.
        this.performLayoutUpdate();

        if (this.config.storageAdapter()) {
            this.storage = new (this.config.storageAdapter())(this.config, this.scosData, this.manifest);
        } else {
            this.storage = new Storage.AllInOneStorage(this.config, this.scosData, this.manifest);
        }
        this.storage.load({callback: this.onceLoaded.bind(this)});
    }
}
